import 'css/src/components/share-video/SpinoffsModal';

import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { AspectRatio } from '@lumen5/luminary';

import { SpinoffsListStage } from './SpinoffsListStage';
import { SpinoffsPreviewStage } from './SpinoffsPreviewStage';

import type { VideoSpinoffPlansResponse } from 'js/src/client';
import { ApiService, TaskResultMessageTypeEnum } from 'js/src/client';
import Button, {
    BUTTON_COLORS,
    BUTTON_PADDING,
    BUTTON_VARIANTS,
} from 'js/src/components/common/ui/Button';
import Modal from 'js/src/components/common/ui/modal';
import { loadTemplates } from 'js/src/data/templates';
import { getVideoPlans, pushVideoPlansAndFetchVideos } from 'js/src/data/video-planner';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';
import { parseScript } from 'js/src/libs/transcript';
import WebSocketClient from 'js/src/libs/websocket-client';
import { LuminaryContextProvider } from 'js/src/luminary';
import { useLuminarySelector } from 'js/src/luminary/luminary-hooks';

export enum ModalStage {
    LIST = 'list',
    PREVIEW = 'preview',
}

export const SpinoffsModal = ({
    videoId,
    isShowing,
    onHidden,
}: {
    videoId: number;
    isShowing: boolean;
    onHidden: VoidFunction;
}) => {
    const dispatch = useAppDispatch();
    const [isError, setIsError] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);
    const [modalStage, setModalStage] = useState<ModalStage>(ModalStage.LIST);
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

    const transcript = useLuminarySelector((luminary) => {
        return parseScript(luminary);
    }, []);

    const getSpinoffs = useCallback(async () => {
        if (!videoId || !transcript) {
            return;
        }
        try {
            setIsError(false);
            setIsFetching(true);
            const refMaterial = transcript;
            await dispatch(loadTemplates());
            const task_response = await ApiService.videoSpinoffPlannerCreate({
                body: {
                    video_id: videoId,
                    reference_material: refMaterial,
                },
            });
            const ws = WebSocketClient.getInstance();
            const plans_response = await ws.waitForMessage<VideoSpinoffPlansResponse>(
                task_response.data.task_id,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'generate_video_spinoff_plans',
            );
            const videoPlans = plans_response.plans;

            analytics.track('Spinoffs Loaded');

            dispatch(pushVideoPlansAndFetchVideos({ videoPlans, referenceMaterial: refMaterial }));
            setHasInitialized(true);
        } catch (error) {
            ErrorReporter.capture(error);
            setIsError(true);
        } finally {
            setIsFetching(false);
        }
    }, [dispatch, transcript, videoId]);

    useEffect(() => {
        if (isShowing && !hasInitialized && !isFetching) {
            getSpinoffs();
        }
    }, [getSpinoffs, isShowing, hasInitialized, isFetching]);

    const onRetryGetSpinoffs = useCallback(() => {
        if (isFetching) {
            return;
        }
        getSpinoffs();
    }, [isFetching, getSpinoffs]);

    // Preview stage state
    const [selectedAspectRatio, setSelectedAspectRatio] = useState<AspectRatio | null>(null);
    const [isOpening, setIsOpening] = useState(false);
    const [savedPlanIds, setSavedPlanIds] = useState<Set<string>>(new Set());

    const videoPlans = useSelector((state: RootState) => getVideoPlans(state));
    const selectedVideoPlan = videoPlans.find((videoPlan) => videoPlan.id === selectedPlanId);

    // Set default format when plan is selected
    useEffect(() => {
        if (selectedVideoPlan && selectedAspectRatio === null) {
            setSelectedAspectRatio(selectedVideoPlan.aspect_ratio as unknown as AspectRatio);
        }
    }, [selectedVideoPlan, selectedAspectRatio]);

    // Reset stage when modal is hidden
    useEffect(() => {
        if (!isShowing) {
            setModalStage(ModalStage.LIST);
            setSelectedPlanId(null);
            setSelectedAspectRatio(null);
        }
    }, [isShowing]);

    const onShowPreview = useCallback(
        (planId: string) => {
            if (savedPlanIds.has(planId)) {
                return;
            }
            setSelectedPlanId(planId);
            setModalStage(ModalStage.PREVIEW);
        },
        [savedPlanIds],
    );

    const onBackToList = useCallback(() => {
        setModalStage(ModalStage.LIST);
        setSelectedPlanId(null);
        setSelectedAspectRatio(null);
    }, []);

    const onAspectRatioChange = useCallback((aspectRatio: AspectRatio) => {
        setSelectedAspectRatio(aspectRatio);
    }, []);

    return (
        <Modal
            showing={isShowing}
            onHidden={onHidden}
            title={
                modalStage === ModalStage.LIST ? (
                    <div className="d-flex align-items-center">Video spinoffs</div>
                ) : (
                    <div className="d-flex align-items-center">
                        <Button
                            onClick={onBackToList}
                            variant={BUTTON_VARIANTS.LINK}
                            color={BUTTON_COLORS.TERTIARY}
                            padding={BUTTON_PADDING.NONE}
                            text=""
                            iconLeft={
                                <i className="material-symbols-outlined icon-l5 icon-l5--medium">
                                    chevron_left
                                </i>
                            }
                            className="me-l5-8"
                        />
                        Preview your spinoff
                    </div>
                )
            }
            className="spinoffs-modal"
        >
            {modalStage === ModalStage.LIST && (
                <SpinoffsListStage
                    isError={isError}
                    isFetching={isFetching}
                    onRetryGetSpinoffs={onRetryGetSpinoffs}
                    onShowPreview={onShowPreview}
                    savedPlanIds={savedPlanIds}
                />
            )}
            {modalStage === ModalStage.PREVIEW && selectedVideoPlan && (
                <LuminaryContextProvider
                    minDimensionResolution={
                        window.screen && window.screen.availWidth <= 900 ? 300 : 500
                    }
                    name="SpinoffPreviewModal"
                >
                    <SpinoffsPreviewStage
                        selectedVideoPlan={selectedVideoPlan}
                        selectedAspectRatio={selectedAspectRatio}
                        isOpening={isOpening}
                        onAspectRatioChange={onAspectRatioChange}
                        onBackToList={onBackToList}
                        onHidden={onHidden}
                        setSavedPlanIds={setSavedPlanIds}
                        setIsOpening={setIsOpening}
                    />
                </LuminaryContextProvider>
            )}
        </Modal>
    );
};
